import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { VisibilityOffIcon, VisibilityOnIcon } from "@/modules/common/assets/icons";
import { Button, Input, ModalBody, ModalFooter } from "@/modules/common/components";
import useNewPassword from "../hooks/useNewPassword";
import { NewPasswordFormData, newPasswordSchema, passwordCaseSchema, passwordLengthSchema, passwordSpecialCharSchema } from "../schemas";

interface NewPasswordStepProps {
  email: string;
  code: string;
  submitLabel?: string;
  onSuccess: (email: string) => void;
}

const PASSWORD_REQUIREMENTS = [
  { label: "pelo menos 8 caracteres", schema: passwordLengthSchema },
  { label: "letras minúsculas e maiúsculas", schema: passwordCaseSchema },
  { label: "pelo menos um número (0-9) e um símbolo", schema: passwordSpecialCharSchema },
];

const NewPasswordStep = ({ email, code, submitLabel = "Redefinir senha", onSuccess }: NewPasswordStepProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const newPassword = useNewPassword();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<NewPasswordFormData>({ resolver: zodResolver(newPasswordSchema), defaultValues: { newPassword: "", confirmPassword: "" } });

  const newPasswordValue = watch("newPassword");

  const returnStyle = (valid: boolean) => (dirtyFields.newPassword ? (valid ? "text-[#159F56]" : "text-[#BA1512]") : "");

  const onSubmit = async (values: NewPasswordFormData) => {
    await newPassword.mutateAsync({ ...values, email, code: Number(code) });
    onSuccess(email);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalBody className="gap-3">
        <div>
          <Input
            label="Nova senha"
            markAsRequired
            type={isPasswordVisible ? "text" : "password"}
            icon={
              <button
                type="button"
                className="flex cursor-pointer items-center"
                onClick={() => setIsPasswordVisible((state) => !state)}
                aria-label={isPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
              >
                {isPasswordVisible ? <VisibilityOnIcon className="w-5 h-5" /> : <VisibilityOffIcon className="w-5 h-5" />}
              </button>
            }
            iconPosition="right"
            data-test="forgot-password-new-password"
            {...register("newPassword")}
          />
          {errors.newPassword && <p className="mt-1 text-xs text-red-500">{errors.newPassword.message}</p>}
        </div>

        <div>
          <p className="text-xs font-semibold text-ui-text">Crie uma senha com:</p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-ui-text">
            {PASSWORD_REQUIREMENTS.map(({ label, schema }) => (
              <li key={label} className={returnStyle(schema.safeParse(newPasswordValue).success)}>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Input
            label="Confirmar senha"
            markAsRequired
            type={isConfirmPasswordVisible ? "text" : "password"}
            icon={
              <button
                type="button"
                className="flex cursor-pointer items-center"
                onClick={() => setIsConfirmPasswordVisible((state) => !state)}
                aria-label={isConfirmPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
              >
                {isConfirmPasswordVisible ? <VisibilityOnIcon className="w-5 h-5" /> : <VisibilityOffIcon className="w-5 h-5" />}
              </button>
            }
            iconPosition="right"
            data-test="forgot-password-confirm-password"
            {...register("confirmPassword")}
          />
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          type="submit"
          className="w-full"
          label={submitLabel}
          disabled={newPassword.isPending}
          loading={newPassword.isPending}
          data-test="forgot-password-submit-new-password"
        />
      </ModalFooter>
    </form>
  );
};

export default NewPasswordStep;
